import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useApi from '../api';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { AppWindow, FileText, Globe, Users } from 'lucide-react';

interface SurfaceDay {
  active_users: number;
  events: number;
  conversations: number;
}

interface ActivityDay {
  date: string;
  active_users: number;
  product: SurfaceDay;
  website: SurfaceDay;
}

interface ActivitySummary {
  dau_today: number;
  avg_dau: number;
  mau: number;
  stickiness: number | null;
}

interface ActivityPerson {
  lead_id: string | null;
  visitor_id: string | null;
  name: string;
  events: number;
  conversations: number;
  active_days: number;
  share: number;
}

interface TopPeopleResponse {
  people: ActivityPerson[];
  total_people: number;
  total_events: number;
  total_conversations: number;
}

interface ActivityPage {
  page: string;
  views: number;
  viewers: number;
  share: number;
}

interface TopPagesResponse {
  pages: ActivityPage[];
  total_pages: number;
  total_views: number;
}

type ActivityRange = 'W' | 'M' | 'Q';

const RANGE_DAYS: Record<ActivityRange, number> = { W: 7, M: 30, Q: 91 };

// Sequential ramps validated (dataviz six-checks, ordinal mode) against the
// app's light (#fffdfa) and dark (#020817) surfaces. Dark mode is its own
// anchor-flipped selection, not an automatic inversion. Product wears green,
// website wears blue — one hue per chart so the surfaces never get confused.
const GREEN_LEVELS = [
  'bg-muted',
  'bg-[#10b981] dark:bg-[#065f46]',
  'bg-[#059669] dark:bg-[#047857]',
  'bg-[#047857] dark:bg-[#10b981]',
  'bg-[#064e3b] dark:bg-[#6ee7b7]',
];
const BLUE_LEVELS = [
  'bg-muted',
  'bg-[#3b82f6] dark:bg-[#1e40af]',
  'bg-[#1d4ed8] dark:bg-[#1d4ed8]',
  'bg-[#1e3a8a] dark:bg-[#3b82f6]',
  'bg-[#172554] dark:bg-[#93c5fd]',
];
const GREEN_BAR = 'bg-[#059669] dark:bg-[#10b981]';
const BLUE_BAR = 'bg-[#1d4ed8] dark:bg-[#3b82f6]';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const tzOffsetMinutes = () => new Date().getTimezoneOffset();

// 'YYYY-MM-DD' as a local date — new Date(str) would parse as UTC midnight
// and shift a day in negative-offset timezones.
const parseDay = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDayLabel = (value: string) =>
  parseDay(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const StatTile: React.FC<{ label: string; value: string; hint: string }> = ({ label, value, hint }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="rounded-xl border bg-card px-4 py-3">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 text-2xl font-semibold">{value}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ShareBarList: React.FC<{
  loading: boolean;
  empty: boolean;
  emptyLabel: string;
  children?: React.ReactNode;
}> = ({ loading, empty, emptyLabel, children }) =>
  loading ? (
    <div className="space-y-2 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-4 rounded bg-muted" />
      ))}
    </div>
  ) : empty ? (
    <div className="text-sm text-muted-foreground">{emptyLabel}</div>
  ) : (
    <div className="space-y-2">{children}</div>
  );

const TopUsersPanel: React.FC<{
  accountId: string;
  startDate: string;
  endDate: string;
  title: string;
}> = ({ accountId, startDate, endDate, title }) => {
  const { get } = useApi();
  const [data, setData] = useState<TopPeopleResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get(
      `/accounts/${accountId}/activity/people?start_date=${startDate}&end_date=${endDate}&tz_offset_minutes=${tzOffsetMinutes()}`
    )
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch((error) => console.error('Error fetching top users:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, startDate, endDate]);

  const maxShare = Math.max(...(data?.people || []).map((p) => p.share), 0);

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold">{title}</div>
        {data && data.total_people > 0 && (
          <div className="text-xs text-muted-foreground">
            {data.total_people} {data.total_people === 1 ? 'person' : 'people'} · {data.total_events} events · {data.total_conversations} conversation{data.total_conversations === 1 ? '' : 's'}
          </div>
        )}
      </div>
      <ShareBarList
        loading={loading}
        empty={!data || data.people.length === 0}
        emptyLabel="No activity in this period."
      >
        {(data?.people || []).map((person) => (
          <div key={person.lead_id || person.visitor_id || person.name} className="space-y-0.5">
            <div className="flex items-baseline justify-between gap-2">
              {person.lead_id ? (
                <Link
                  to={`/contacts/${person.lead_id}`}
                  className="truncate text-sm font-medium hover:underline"
                >
                  {person.name}
                </Link>
              ) : (
                <span className="truncate text-sm">{person.name}</span>
              )}
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {Math.round(person.share * 100)}%
              </span>
            </div>
            <div className="flex items-center">
              <div
                className={`h-3 rounded-r-[4px] ${GREEN_BAR}`}
                style={{ width: `${maxShare ? Math.max((person.share / maxShare) * 100, 3) : 3}%` }}
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              {person.events} events · {person.conversations} convo{person.conversations === 1 ? '' : 's'} · {person.active_days} active day{person.active_days === 1 ? '' : 's'}
            </div>
          </div>
        ))}
      </ShareBarList>
    </div>
  );
};

const TopPagesPanel: React.FC<{
  accountId: string;
  startDate: string;
  endDate: string;
  title: string;
}> = ({ accountId, startDate, endDate, title }) => {
  const { get } = useApi();
  const [data, setData] = useState<TopPagesResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get(
      `/accounts/${accountId}/activity/pages?start_date=${startDate}&end_date=${endDate}&tz_offset_minutes=${tzOffsetMinutes()}`
    )
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch((error) => console.error('Error fetching top pages:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, startDate, endDate]);

  const maxShare = Math.max(...(data?.pages || []).map((p) => p.share), 0);

  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold">{title}</div>
        {data && data.total_views > 0 && (
          <div className="text-xs text-muted-foreground">
            {data.total_views} page view{data.total_views === 1 ? '' : 's'} across {data.total_pages} page{data.total_pages === 1 ? '' : 's'}
          </div>
        )}
      </div>
      <ShareBarList
        loading={loading}
        empty={!data || data.pages.length === 0}
        emptyLabel="No page views in this period."
      >
        {(data?.pages || []).map((page) => (
          <div key={page.page} className="space-y-0.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-medium" title={page.page}>
                {page.page}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {Math.round(page.share * 100)}%
              </span>
            </div>
            <div className="flex items-center">
              <div
                className={`h-3 rounded-r-[4px] ${BLUE_BAR}`}
                style={{ width: `${maxShare ? Math.max((page.share / maxShare) * 100, 3) : 3}%` }}
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              {page.views} view{page.views === 1 ? '' : 's'} · {page.viewers} viewer{page.viewers === 1 ? '' : 's'}
            </div>
          </div>
        ))}
      </ShareBarList>
    </div>
  );
};

interface SurfaceCell {
  date: string;
  active: number;
  events: number;
  conversations: number;
}

const SurfaceGrid: React.FC<{
  accountId: string;
  title: string;
  icon: React.ReactNode;
  cells: SurfaceCell[];
  range: ActivityRange;
  levelClasses: string[];
  clickHint: string;
  periodButtonLabel: string;
  periodButtonIcon: React.ReactNode;
  rangeTitle: string;
  renderPanel: (startDate: string, endDate: string, title: string) => React.ReactNode;
}> = ({ accountId, title, icon, cells, range, levelClasses, clickHint, periodButtonLabel, periodButtonIcon, rangeTitle, renderPanel }) => {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [periodOpen, setPeriodOpen] = useState(false);

  useEffect(() => setSelectedDay(null), [range, accountId]);

  const maxScore = useMemo(
    () => Math.max(...cells.map((cell) => cell.events + cell.conversations), 0),
    [cells]
  );
  const levelFor = (cell: SurfaceCell) => {
    const score = cell.events + cell.conversations;
    if (!score || !maxScore) return 0;
    return Math.min(4, Math.max(1, Math.ceil((score / maxScore) * 4)));
  };

  // GitHub layout: columns are weeks, rows are weekdays (Sun..Sat). Pad the
  // first week so every column starts on Sunday.
  const weeks = useMemo(() => {
    if (!cells.length) return [] as (SurfaceCell | null)[][];
    const padded: (SurfaceCell | null)[] = [
      ...Array(parseDay(cells[0].date).getDay()).fill(null),
      ...cells,
    ];
    const result: (SurfaceCell | null)[][] = [];
    for (let i = 0; i < padded.length; i += 7) {
      result.push(padded.slice(i, i + 7));
    }
    return result;
  }, [cells]);

  const monthLabels = useMemo(
    () =>
      weeks.map((week, index) => {
        const firstDay = week.find(Boolean) as SurfaceCell | undefined;
        if (!firstDay) return '';
        const month = parseDay(firstDay.date).toLocaleDateString('en-US', { month: 'short' });
        const previousWeek = index > 0 ? (weeks[index - 1].find(Boolean) as SurfaceCell | undefined) : undefined;
        const previousMonth = previousWeek
          ? parseDay(previousWeek.date).toLocaleDateString('en-US', { month: 'short' })
          : null;
        return month !== previousMonth ? month : '';
      }),
    [weeks]
  );

  const startDate = cells[0]?.date;
  const endDate = cells[cells.length - 1]?.date;
  const cellSize = range === 'Q' ? 'h-5 w-5' : range === 'M' ? 'h-7 w-7' : 'h-12 w-12';

  const renderCell = (cell: SurfaceCell | null, key: React.Key) => {
    if (!cell) return <div key={key} className={cellSize} />;
    const score = cell.events + cell.conversations;
    return (
      <Popover
        key={key}
        open={selectedDay === cell.date}
        onOpenChange={(open) => setSelectedDay(open ? cell.date : null)}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`${formatDayLabel(cell.date)}: ${cell.active} active`}
                className={`${cellSize} rounded-[3px] ${levelClasses[levelFor(cell)]} transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
              />
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs">
              <div className="font-semibold">{formatDayLabel(cell.date)}</div>
              <div>
                {cell.active} active · {cell.events} events · {cell.conversations} convo{cell.conversations === 1 ? '' : 's'}
              </div>
              {score > 0 && <div className="text-muted-foreground">{clickHint}</div>}
            </div>
          </TooltipContent>
        </Tooltip>
        <PopoverContent align="center" side="top" className="w-80">
          {renderPanel(cell.date, cell.date, `${periodButtonLabel} — ${formatDayLabel(cell.date)}`)}
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div>
      <div className="mb-2 flex items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          {icon} {title}
        </span>
        {startDate && endDate && (
          <Popover open={periodOpen} onOpenChange={setPeriodOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                {periodButtonIcon} {periodButtonLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              {renderPanel(startDate, endDate, `${periodButtonLabel} ${rangeTitle}`)}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <TooltipProvider delayDuration={150}>
        {range === 'W' ? (
          <div className="flex gap-2">
            {cells.map((cell) => (
              <div key={cell.date} className="flex flex-col items-center gap-1">
                {renderCell(cell, cell.date)}
                <span className="text-[10px] text-muted-foreground">
                  {WEEKDAY_LABELS[parseDay(cell.date).getDay()]}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <div className="inline-flex flex-col gap-[3px]">
              <div className="flex gap-[3px]">
                {monthLabels.map((label, index) => (
                  <div key={index} className={`${cellSize} text-[10px] leading-3 text-muted-foreground overflow-visible whitespace-nowrap`}>
                    {label}
                  </div>
                ))}
              </div>
              {[0, 1, 2, 3, 4, 5, 6].map((weekday) => (
                <div key={weekday} className="flex gap-[3px]">
                  {weeks.map((week, weekIndex) => renderCell(week[weekday] ?? null, `${weekIndex}-${weekday}`))}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          Less
          {levelClasses.map((levelClass, index) => (
            <span key={index} className={`h-2.5 w-2.5 rounded-[3px] ${levelClass}`} />
          ))}
          More
        </div>
      </TooltipProvider>
    </div>
  );
};

const AccountActivity: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { get } = useApi();
  const [range, setRange] = useState<ActivityRange>('Q');
  const [days, setDays] = useState<ActivityDay[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get(
      `/accounts/${accountId}/activity/daily?days=${RANGE_DAYS[range]}&tz_offset_minutes=${tzOffsetMinutes()}`
    )
      .then((response) => {
        if (cancelled) return;
        setDays(response.data.days || []);
        setSummary(response.data.summary || null);
      })
      .catch((error) => console.error('Error fetching account activity:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, range]);

  const rangeTitle = range === 'W' ? 'this week' : range === 'M' ? 'this month' : 'this quarter';

  const productCells = useMemo(
    () =>
      days.map((day) => ({
        date: day.date,
        active: day.product.active_users,
        events: day.product.events,
        conversations: day.product.conversations,
      })),
    [days]
  );
  const websiteCells = useMemo(
    () =>
      days.map((day) => ({
        date: day.date,
        active: day.website.active_users,
        events: day.website.events,
        conversations: day.website.conversations,
      })),
    [days]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Activity</h2>
        <div className="flex rounded-lg border p-0.5">
          {(['W', 'M', 'Q'] as ActivityRange[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setRange(option)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                range === option ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4">
        {summary && (
          <div className="mb-4 grid grid-cols-3 gap-3">
            <StatTile
              label="DAU"
              value={String(summary.dau_today)}
              hint="People active today: an Aimdoc conversation or in-app activity"
            />
            <StatTile
              label="MAU"
              value={String(summary.mau)}
              hint="Distinct people active in the last 30 days"
            />
            <StatTile
              label="DAU / MAU"
              value={summary.stickiness != null ? `${Math.round(summary.stickiness * 100)}%` : '—'}
              hint={`Stickiness: average DAU (${summary.avg_dau}) ÷ MAU over the last 30 days`}
            />
          </div>
        )}
        {loading ? (
          <div className="h-24 animate-pulse rounded bg-muted" />
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <SurfaceGrid
              accountId={accountId}
              title="Product"
              icon={<AppWindow className="h-3.5 w-3.5" />}
              cells={productCells}
              range={range}
              levelClasses={GREEN_LEVELS}
              clickHint="Click for top users"
              periodButtonLabel="Top users"
              periodButtonIcon={<Users className="h-3.5 w-3.5" />}
              rangeTitle={rangeTitle}
              renderPanel={(startDate, endDate, title) => (
                <TopUsersPanel accountId={accountId} startDate={startDate} endDate={endDate} title={title} />
              )}
            />
            <SurfaceGrid
              accountId={accountId}
              title="Website"
              icon={<Globe className="h-3.5 w-3.5" />}
              cells={websiteCells}
              range={range}
              levelClasses={BLUE_LEVELS}
              clickHint="Click for top pages"
              periodButtonLabel="Top pages"
              periodButtonIcon={<FileText className="h-3.5 w-3.5" />}
              rangeTitle={rangeTitle}
              renderPanel={(startDate, endDate, title) => (
                <TopPagesPanel accountId={accountId} startDate={startDate} endDate={endDate} title={title} />
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountActivity;
